import React, { Fragment, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
    BulkTrainingAssignmentModal,
    ConfirmationModal,
    LoadingSpinner,
    TrainingFormModal
} from '../components'
import { Button, Card } from '../components/ui'
import { useResourcePermissions } from '../hooks/useResourcePermissions'
import {
    API_BASE_URL,
    BulkTrainingAssignmentFormMeta,
    ConfirmationModalMeta,
    LoadingSpinnerMeta,
    TrainingFormModalMeta,
    TrainingSequenceEditMeta,
    UpdateTrainingFormMeta
} from '../data'
import { AdminTrainingsMetaProps, Training, TrainingContent } from '../data/types'
import {
    AudioIcon,
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronUpIcon,
    ClockIcon,
    DeleteIcon,
    DocumentIcon,
    EditIcon,
    ExternalLinkIcon,
    InteractiveIcon,
    ConstructionIcon,
    PlusIcon,
    QuizIcon,
    VideoIcon,
    EyeIcon,
    DownloadIcon
} from '../icons'
import { TrainingSequenceEditModal } from '../components/TrainingSequenceEditModal'

import { useAuth } from '../components'

type Props = {
    meta: AdminTrainingsMetaProps
}

const getModuleIcon = (type: string, className?: string) => {
    switch (type) {
        case 'video':
            return <VideoIcon className={className} />
        case 'document':
            return <DocumentIcon className={className} />
        case 'audio':
            return <AudioIcon className={className} />
        case 'quiz':
            return <QuizIcon className={className} />
        case 'interactive':
            return <InteractiveIcon className={className} />
        case 'external':
            return <ExternalLinkIcon className={className} />
        case 'coming_soon':
            return <ConstructionIcon className={className} />
        default:
            return <DocumentIcon className={className} />
    }
}

const parseContent = (content: TrainingContent | string): TrainingContent | null => {
    if (typeof content === 'string') {
        try {
            return JSON.parse(content) as TrainingContent
        } catch {
            return null
        }
    }
    return content
}

export const AdminTrainings = (props: Props) => {
    const { meta } = props
    const navigate = useNavigate()
    const { canEditTraining, canDeleteTraining, canCreateTraining } = useResourcePermissions()
    const [loading, setLoading] = useState(true)
    const [trainings, setTrainings] = useState<Training[]>([])
    const [error, setError] = useState<string | null>(null)
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [editingTraining, setEditingTraining] = useState<Training | null>(null)
    const [deletingTraining, setDeletingTraining] = useState<Training | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const [expandedTrainings, setExpandedTrainings] = useState<Set<number>>(new Set())
    const [isBulkTrainingModalOpen, setIsBulkTrainingModalOpen] = useState(false)
    const [selectedTraining, setSelectedTraining] = useState<Training | null>(null)
    const [isEditSequenceModalOpen, setIsEditSequenceModalOpen] = useState(false)
    const [exportLoading, setExportLoading] = useState<number | null>(null); // Track which training is exporting

    const { user } = useAuth();

    const canExport = user.permissions?.includes('export_trainings');

    useEffect(() => {
        fetchTrainings()
    }, [])

    const fetchTrainings = async () => {
        setLoading(true)
        setError(null)

        try {
            const response = await fetch(`${API_BASE_URL}/training`, { credentials: 'include' })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            const result = await response.json()
            let data = result.value || result

            if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                data = data[0]
            }

            setTrainings(data)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching trainings')
        } finally {
            setLoading(false)
        }
    }

    const handleCreateTraining = async (trainingData: any) => {
        const trainingDataSeq = {
            ...trainingData,
            'sequence_no': Math.max(...trainings.map(t => t.sequence_no ?? 0)) + 1
        }

        const response = await fetch(`${API_BASE_URL}/training`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(trainingDataSeq)
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        await fetchTrainings()
    }

    const handleUpdateTraining = async (trainingData: any) => {
        if (!editingTraining) return

        const response = await fetch(`${API_BASE_URL}/training/${editingTraining.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(trainingData)
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        await fetchTrainings()
        setEditingTraining(null)
    }

    const handleDeleteTraining = async () => {
        if (!deletingTraining) return

        setIsDeleting(true)
        try {
            const response = await fetch(`${API_BASE_URL}/training/${deletingTraining.id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            await fetchTrainings()
            setDeletingTraining(null)
        } catch (err) {
            console.error('Error deleting training:', err)
        } finally {
            setIsDeleting(false)
        }
    }

    const toggleTrainingExpansion = (trainingId: number) => {
        const newExpanded = new Set(Array.from(expandedTrainings))
        if (newExpanded.has(trainingId)) {
            newExpanded.delete(trainingId)
        } else {
            newExpanded.add(trainingId)
        }
        setExpandedTrainings(newExpanded)
    }

    const handleBulkTrainingAssignClick = (training: Training) => {
        setSelectedTraining(training)
        setIsBulkTrainingModalOpen(true)
    }

    const handleBulkTrainingAssignClose = () => {
        setSelectedTraining(null)
        setIsBulkTrainingModalOpen(false)
    }

    const handleEditSequenceClickConfirm = async (sortedTrainings: Training[]) => {
        const trainingIds = [];
        const sequence = [];

        sortedTrainings.forEach((t, index) => {
            trainingIds.push(t.id)
            sequence.push(index)
        })

        const response = await fetch(`${API_BASE_URL}/training/sequence`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                'training': trainingIds,
                'sequence': sequence
            })
        });

        if (!response.ok) {
            throw new Error('Failed to save sequence');
        }

        setTrainings(sortedTrainings)
    }

    const handleExportTrainingSummary = () => {
        const headers = [
            'ID', 'Name', 'Description', 'Mandatory', 'Policy',
            'Sequence No', 'Module Count', 'Total Duration (min)',
            'Total Questions', 'Created At'
        ];

        const rows = trainings.map(training => {
            const parsedContent = parseContent(training.content);
            const modules = parsedContent?.modules || [];

            const totalDuration = modules.reduce((sum, mod) => sum + (mod.duration || 0), 0);
            const totalQuestions = modules.reduce((sum, mod) => {
                if (mod.questions && Array.isArray(mod.questions)) {
                    return sum + mod.questions.length;
                }
                return sum;
            }, 0);

            const escapeCsv = (str: string | number | boolean | null | undefined) => {
                if (str === null || str === undefined) return '';
                const stringValue = String(str);
                if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                    return `"${stringValue.replace(/"/g, '""')}"`;
                }
                return stringValue;
            };

            return [
                escapeCsv(training.id),
                escapeCsv(training.name),
                escapeCsv(training.description),
                escapeCsv(training.mandatory ? 'Yes' : 'No'),
                escapeCsv(training.policy ? 'Yes' : 'No'),
                escapeCsv(training.sequence_no),
                escapeCsv(modules.length),
                escapeCsv(totalDuration),
                escapeCsv(totalQuestions),
                escapeCsv(training.created_at ? new Date(training.created_at).toLocaleDateString() : '')
            ].join(',');
        });

        const csvContent = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        const date = new Date().toISOString().split('T')[0];
        link.setAttribute('href', url);
        link.setAttribute('download', `training_summary_${date}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // New Function: Export Volunteers assigned to a specific Training
    const handleExportTrainingVolunteers = async (training: Training) => {
        setExportLoading(training.id);
        try {
            const allVolunteers = [];
            let currentPage = 1;
            let lastPage = 1;
            const perPage = 500; // Fetch in chunks

            // Fetch all pages
            do {
                // Adjust endpoint query params as needed based on your controller
                const response = await fetch(
                    `${API_BASE_URL}/dashboard/stats/group/training-export?training_name=${encodeURIComponent(training.name)}&per_page=${perPage}&page=${currentPage}`,
                    { credentials: 'include' }
                );

                if (!response.ok) throw new Error('Failed to fetch training report');

                const result = await response.json();
                console.log("Result:", result);
                const data = result.data || result;
                lastPage = result.last_page || 1;

                allVolunteers.push(...data);
                currentPage++;
            } while (currentPage <= lastPage);

            if (allVolunteers.length === 0) {
                alert("No volunteers found for this training.");
                return;
            }

            // Generate CSV
            const headers = [
                'ID', 'First Name', 'Last Name', 'Email', 'Phone',
                'Age Band', 'Gender', 'Jamat Khana', 'Ismaili', 'Multi-faith Family',
                'Family Origin', 'Languages', 'Service Record',
                'Completion Status'
            ];

            const rows = allVolunteers.map((item: any) => {
                const escapeCsv = (str: any) => {
                    if (str === null || str === undefined) return '';
                    const stringValue = String(str).replace(/[\r\n]+/g, ' ').trim();
                    if (stringValue.startsWith('=')) return stringValue; // Excel formula safety
                    if (stringValue.includes(',') || stringValue.includes('"')) {
                        return `"${stringValue.replace(/"/g, '""')}"`;
                    }
                    return stringValue;
                };

                // Map completion status (Assuming backend returns 1/0 or boolean or specific string)
                let statusText = 'Pending';
                if (item.completion_status === 1 || item.completion_status === true || item.completion_status === '1') {
                    statusText = 'Completed';
                }

                return [
                    item.csid ? `="${item.csid}"` : '',
                    escapeCsv(item.first_name),
                    escapeCsv(item.last_name),
                    escapeCsv(item.email),
                    item.phone ? `="${item.phone}"` : '',
                    escapeCsv(item.age_band),
                    escapeCsv(item.gender),
                    escapeCsv(item.jamat_khana),
                    item.ismaili ? 'Yes' : 'No',
                    item.multi_faith_family ? 'Yes' : 'No',
                    escapeCsv(item.family_origin),
                    escapeCsv(item.languages),
                    escapeCsv(item.service_record),
                    statusText
                ].join(',');
            });

            const csvContent = [headers.join(','), ...rows].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            const date = new Date().toISOString().split('T')[0];
            const safeName = training.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();

            link.setAttribute('href', url);
            link.setAttribute('download', `${safeName}_volunteers_${date}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (err) {
            console.error("Export failed:", err);
            // Ideally show a toast or error state here
        } finally {
            setExportLoading(null);
        }
    };

    return (
        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div>
                        <Link
                            to="/admin"
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                            <ChevronLeftIcon className="w-5 h-5 me-2" />
                            Back to Admin Hub
                        </Link>
                    </div>
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                            {meta.description}
                        </p>

                        <div className="flex justify-center gap-4">
                            {canCreateTraining() && (
                                <Button onClick={() => setIsCreateModalOpen(true)}>
                                    <PlusIcon className="w-5 h-5" />
                                    Create New Training
                                </Button>
                            )}

                            {trainings.length > 0 && canExport && (
                                <Button onClick={handleExportTrainingSummary} variant="outline" color="primary">
                                    <DownloadIcon className="w-5 h-5" />
                                    Export Summary
                                </Button>
                            )}
                        </div>
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    {error && (
                        <div className="max-w-2xl mx-auto">
                            <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {!loading && !error && trainings.length === 0 && (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                            <p className="text-xl">{meta.noTrainingsMessage}</p>
                        </div>
                    )}

                    {!loading && !error && trainings.length > 0 && (

                        <div className="max-w-4xl mx-auto space-y-4">
                            {canCreateTraining() && <div className="flex justify-end">
                                <Button onClick={() => setIsEditSequenceModalOpen(true)} color='success'>
                                    <EditIcon className="w-5 h-5" />
                                    Edit Training Sequence
                                </Button>
                            </div>}
                            {trainings.map((training) => {
                                const parsedContent = parseContent(training.content)
                                const modules = parsedContent?.modules || []
                                const totalDuration = modules.reduce((sum, mod) => sum + (mod.duration || 0), 0)
                                const totalQuestions = modules.reduce((sum, mod) => {
                                    if (mod.questions && Array.isArray(mod.questions)) {
                                        return sum + mod.questions.length
                                    }
                                    return sum
                                }, 0)
                                const isExpanded = expandedTrainings.has(training.id)

                                return (
                                    <Card key={training.id}>
                                        <div className="p-6">
                                            <div className="flex items-start">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                                            {training.name}
                                                        </h2>
                                                        {training.mandatory && (
                                                            <span className="px-2 py-1 text-xs font-semibold bg-danger-100 dark:bg-danger-900 text-danger-700 dark:text-danger-300 rounded-full">
                                                                MANDATORY
                                                            </span>
                                                        )}
                                                        {training.policy && (
                                                            <span className="px-2 py-1 text-xs font-semibold bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full">
                                                                POLICY
                                                            </span>
                                                        )}
                                                    </div>
                                                    {training.description && (
                                                        <p className="text-gray-600 dark:text-gray-400 mb-2">
                                                            {training.description}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex gap-2 ms-4">
                                                    {modules.length > 0 && (
                                                        <Button
                                                            onClick={() => toggleTrainingExpansion(training.id)}
                                                            variant="icon"
                                                            color="neutral"
                                                            size="sm"
                                                        >
                                                            {isExpanded ? (
                                                                <ChevronUpIcon className="w-5 h-5" />
                                                            ) : (
                                                                <ChevronDownIcon className="w-5 h-5" />
                                                            )}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        onClick={() => navigate(`/trainings/${training.id}`)}
                                                        variant="icon"
                                                        color="primary"
                                                        size="sm"
                                                    >
                                                        <EyeIcon className="w-5 h-5" />
                                                    </Button>
                                                    {canEditTraining(training) && <Button
                                                        onClick={() => setEditingTraining(training)}
                                                        variant="icon"
                                                        color="primary"
                                                        size="sm"
                                                    >
                                                        <EditIcon className="w-5 h-5" />
                                                    </Button>}
                                                    {canDeleteTraining(training) && <Button
                                                        onClick={() => setDeletingTraining(training)}
                                                        variant="icon"
                                                        color="danger"
                                                        size="sm"
                                                    >
                                                        <DeleteIcon className="w-5 h-5" />
                                                    </Button>}
                                                </div>
                                            </div>

                                            {modules.length > 0 && (
                                                <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-2">
                                                    <span>{modules.length} module{modules.length !== 1 ? 's' : ''}</span>
                                                    {totalDuration > 0 && (
                                                        <div className="flex items-center gap-1">
                                                            <ClockIcon className="w-4 h-4" />
                                                            <span>{totalDuration} min</span>
                                                        </div>
                                                    )}
                                                    {totalQuestions > 0 && (
                                                        <div className="flex items-center gap-1">
                                                            <QuizIcon className="w-4 h-4" />
                                                            <span>{totalQuestions} question{totalQuestions !== 1 ? 's' : ''}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {modules.length > 0 && isExpanded && (
                                                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                                    <div className="space-y-2">
                                                        {modules
                                                            .map((module, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                                                                >
                                                                    <div className="flex-shrink-0 text-primary-600 dark:text-primary-400">
                                                                        {getModuleIcon(module.type, "w-5 h-5")}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                                            {module.title}
                                                                        </p>
                                                                        {module.description && (
                                                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                                                {module.description}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    {module.questions && module.questions.length > 0 && (
                                                                        <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                                                                            <QuizIcon className="w-3 h-3" />
                                                                            <span>{module.questions.length} question{module.questions.length !== 1 ? 's' : ''}</span>
                                                                        </div>
                                                                    )}
                                                                    {module.duration && (
                                                                        <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                                                                            <ClockIcon className="w-3 h-3" />
                                                                            <span>{module.duration} min</span>
                                                                        </div>
                                                                    )}
                                                                    {module.required && (
                                                                        <span className="text-xs bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 px-2 py-1 rounded">
                                                                            Required
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex justify-end mt-4 gap-2">
                                                {/* Export Report Button */}
                                                {(canExport && (<Button
                                                    color="primary"
                                                    variant="outline"
                                                    onClick={() => handleExportTrainingVolunteers(training)}
                                                    disabled={exportLoading === training.id}
                                                >
                                                    {exportLoading === training.id ? (
                                                        <span className="flex items-center gap-2">
                                                            <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                                                            Exporting...
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center gap-2">
                                                            <DownloadIcon className="w-4 h-4" />
                                                            Export Report
                                                        </span>
                                                    )}
                                                </Button>))}

                                                <Button
                                                    color="success"
                                                    onClick={() => handleBulkTrainingAssignClick(training)}
                                                >
                                                    {meta.assignVolunteersMessage}
                                                </Button>
                                            </div>

                                        </div>
                                    </Card>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Create Modal */}
            <TrainingFormModal
                meta={TrainingFormModalMeta}
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSubmit={handleCreateTraining}
            />

            {/* Edit Modal */}
            {
                editingTraining && (
                    <TrainingFormModal
                        meta={{ ...TrainingFormModalMeta, ...UpdateTrainingFormMeta }}
                        isOpen={!!editingTraining}
                        onClose={() => setEditingTraining(null)}
                        onSubmit={handleUpdateTraining}
                        initialTraining={editingTraining}
                    />
                )
            }

            <BulkTrainingAssignmentModal
                meta={BulkTrainingAssignmentFormMeta}
                isOpen={isBulkTrainingModalOpen}
                onClose={handleBulkTrainingAssignClose}
                training={selectedTraining}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={!!deletingTraining}
                onConfirm={handleDeleteTraining}
                onCancel={() => setDeletingTraining(null)}
                title="Delete Training"
                message={`Are you sure you want to delete "${deletingTraining?.name}"? This action cannot be undone.`}
                isLoading={isDeleting}
            />

            <TrainingSequenceEditModal
                meta={TrainingSequenceEditMeta}
                isOpen={isEditSequenceModalOpen}
                trainings={trainings}
                onClose={() => setIsEditSequenceModalOpen(false)}
                onSubmit={handleEditSequenceClickConfirm}
            />

        </Fragment >
    )
}